import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';

// Fetch all MCP servers from the server and update the state with the retrieved data
function* getAllMCPServers() {
  const { error, response } = yield call(getCall, '/automation/assistant/mcp/servers');
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ALL_MCP_SERVERS', payload: response['data']['body'] });
}

// Create a new MCP server configuration with the provided data
function* createMCPServer({ payload }) {
  const { error, response } = yield call(postCall, { path: '/automation/assistant/mcp/servers', payload });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create MCP server');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
  }
}

// Update an existing MCP server configuration by its name with the provided data
function* updateMCPServer({ payload }) {
  const { serverName, data } = payload;
  const { error, response } = yield call(putCall, { path: `/automation/assistant/mcp/servers/${serverName}`, payload: data });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update MCP server');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
  }
}

// Delete an MCP server by its name and refresh the server list upon success
function* deleteMCPServer({ payload }) {
  const { error, response } = yield call(deleteCall, `/automation/assistant/mcp/servers/${payload}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete MCP server');
  else if (response) EventBus.publish('success', response['data']['message']);
  yield put({ type: 'GET_ALL_MCP_SERVERS' });
}

// Connect to an MCP server and refresh server/tool lists upon success
function* connectMCPServer({ payload }) {
  const { error, response } = yield call(postCall, { path: `/automation/assistant/mcp/servers/${payload}/connect`, payload: {} });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to connect to MCP server');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
    yield put({ type: 'GET_ALL_MCP_TOOLS' });
  }
}

// Disconnect from an MCP server and refresh server/tool lists
function* disconnectMCPServer({ payload }) {
  const { error, response } = yield call(postCall, { path: `/automation/assistant/mcp/servers/${payload}/disconnect`, payload: {} });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to disconnect from MCP server');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
    yield put({ type: 'GET_ALL_MCP_TOOLS' });
  }
}


// Execute an MCP tool with the provided arguments and handle the response
function* executeMCPTool({ payload }) {
  const { serverName, toolName, args } = payload;
  yield put({ type: 'SET_EXECUTING_TOOL', payload: toolName });
  const { error, response } = yield call(postCall, { path: `/automation/assistant/mcp/servers/${serverName}/tools/${toolName}/execute`, payload: args });
  if (error) {
    yield put({ type: 'SET_TOOL_EXECUTION_RESULT', payload: { error: error['response']?.['data']?.['message'] || 'Failed to execute tool' } });
    return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to execute tool');
  }
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'SET_TOOL_EXECUTION_RESULT', payload: response['data']['body'] });
  }
}

// Update tool customization settings for a specific tool
function* updateToolCustomization({ payload }) {
  const { serverName, toolName, data } = payload;
  const { error, response } = yield call(putCall, { path: `/automation/assistant/mcp/servers/${serverName}/tools/${toolName}`, payload: data });
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update tool customization');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
  }
}

// Remove tool customization settings for a specific tool
function* removeToolCustomization({ payload }) {
  const { serverName, toolName } = payload;
  const { error, response } = yield call(deleteCall, `/automation/assistant/mcp/servers/${serverName}/tools/${toolName}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to remove tool customization');
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'GET_ALL_MCP_SERVERS' });
  }
}

// Send chat message to AI assistant
function* sendChatMessage({ payload }) {
  const { message, threadId } = payload;
  try {
    const response = yield axios.post('/automation/assistant/chat', { message, threadId });

    // Check if response is streaming
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('text/event-stream')) {
      
      // Parse SSE data from string response
      const sseData = response['data'];
      const lines = sseData.split('\n').filter(line => line.trim() !== '');
      
      let fullMessage = '';
      let suggestedTool = null;
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          
          try {
            const parsed = JSON.parse(data);
            if (parsed['type'] === 'chunk') fullMessage += parsed['content'];
            else if (parsed['type'] === 'complete') {
              fullMessage = parsed['message'];
              suggestedTool = parsed['suggestedTool'];
            }
          } catch (e) { console.error('Error parsing SSE data:', e); }
        }
      }
      
      // Send the complete message to the callback
      if (successCallback && fullMessage)
        successCallback({ type: 'ai_response', message: fullMessage, suggestedTool: suggestedTool });
    } else {
      // Handle regular JSON response
      const data = response['data'];
      if (data['status'] === 'success' && successCallback) successCallback(data['body']);
    }
  } catch (error) { EventBus.publish('error', error['message'] || 'Failed to send message'); }
}

// Fetch all threads for the current user
function* getThreads() {
  yield put({ type: 'SET_THREADS_LOADING', payload: true });
  const { error, response } = yield call(getCall, '/automation/assistant/threads');
  if (error) {
    yield put({ type: 'SET_THREADS_LOADING', payload: false });
    return EventBus.publish('error', error['response']['data']['message']);
  }
  else if (response) yield put({ type: 'SET_THREADS', payload: response['data']['body'] });
}

// Fetch messages for a specific thread
function* getThreadMessages({ payload }) {
  const { error, response } = yield call(getCall, `/automation/assistant/threads/${payload}/messages`);
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    yield put({ 
      type: 'SET_THREAD_MESSAGES', 
      payload: { 
        threadId: payload, 
        messages: response['data']['body'] 
      } 
    });
  }
}

// Delete a thread
function* deleteThread({ payload }) {
  const { error, response } = yield call(deleteCall, `/automation/assistant/threads/${payload}`);
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    EventBus.publish('success', response['data']['message']);
    yield put({ type: 'REMOVE_THREAD', payload });
  }
}

// Export thread conversation
function* exportThread({ payload }) {
  const { threadId, format } = payload;
  const { error, response } = yield call(getCall, `/automation/assistant/threads/${threadId}/export?format=${format || 'json'}`);
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    // Handle file download
    const data = response['data']['body'];
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `thread-${threadId}-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
    EventBus.publish('success', 'Thread exported successfully');
  }
}

// Transcribe audio to text 
function* transcribeAudio({ payload }) {
  const { audio, onSuccess, onError } = payload;
  
  try {
    const { error, response } = yield call(postCall, { 
      path: '/automation/assistant/speech-to-text', 
      payload: { audio }
    });
    
    if (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to transcribe audio';
      if (onError) onError(errorMessage);
    } else if (response) {
      const transcribedText = response?.data?.body?.text;
      if (onSuccess && transcribedText) {
        onSuccess(transcribedText);
      }
    }
  } catch (err) {
    const errorMessage = 'Failed to transcribe audio';
    if (onError) onError(errorMessage);
  }
}

// Update message feedback (like/dislike)
function* updateMessageFeedback({ payload }) {
  console.log('updateMessageFeedback', payload);
  const { messageId, feedback } = payload;
  const { error, response } = yield call(putCall, { 
    path: `/automation/assistant/messages/${messageId}/feedback`, 
    payload: { feedback } 
  });
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    EventBus.publish('success');
    yield put({ 
      type: 'UPDATE_MESSAGE_FEEDBACK_SUCCESS', 
      payload: { messageId, feedback } 
    });
  }
}

// Watcher saga for handling assistant-related actions
function* actionWatcher() {
  yield takeEvery('CREATE_MCP_SERVER', createMCPServer);
  yield takeEvery('GET_ALL_MCP_SERVERS', getAllMCPServers);
  yield takeEvery('UPDATE_MCP_SERVER', updateMCPServer);
  yield takeEvery('DELETE_MCP_SERVER', deleteMCPServer);
  yield takeEvery('CONNECT_MCP_SERVER', connectMCPServer);
  yield takeEvery('DISCONNECT_MCP_SERVER', disconnectMCPServer);
  yield takeEvery('EXECUTE_MCP_TOOL', executeMCPTool);
  yield takeEvery('UPDATE_TOOL_CUSTOMIZATION', updateToolCustomization);
  yield takeEvery('REMOVE_TOOL_CUSTOMIZATION', removeToolCustomization);
  yield takeEvery('SEND_CHAT_MESSAGE', sendChatMessage);
  yield takeEvery('GET_THREADS', getThreads);
  yield takeEvery('GET_THREAD_MESSAGES', getThreadMessages);
  yield takeEvery('DELETE_THREAD', deleteThread);
  yield takeEvery('EXPORT_THREAD', exportThread);
  yield takeEvery('TRANSCRIBE_AUDIO', transcribeAudio);
  yield takeEvery('UPDATE_MESSAGE_FEEDBACK', updateMessageFeedback);
}

// Root saga for managing all assistant-related actions
export default function* rootSaga() { yield all([actionWatcher()]) }

// Helper function to make POST requests to the server
function postCall({ path, payload, headers = {} }) {
  return axios
    .post(path, payload, {
      headers: {
        'Content-Type': payload instanceof FormData ? 'multipart/form-data' : 'application/json',
        ...headers,
      },
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make GET requests to the server
function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make DELETE requests to the server
function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make PUT requests to the server
function putCall({ path, payload, headers = {} }) {
  return axios
    .put(path, payload, {
      headers: {
        'Content-Type': payload instanceof FormData ? 'multipart/form-data' : 'application/json',
        ...headers,
      },
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}